import { useEffect, useMemo, useState } from 'react'

import { api, downloadBlob } from '../api/client'
import type { DeviceLog } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner, formatDateTime, timeAgo } from './common'

/**
 * Il **log di un dispositivo su richiesta**: un agente d'inventario o una
 * sonda. «Richiedi» apre la richiesta; il dispositivo la vede al prossimo
 * contatto (keep-alive dell'agente entro un minuto, heartbeat della sonda
 * entro mezzo minuto), legge la coda del suo registro e la consegna; qui si
 * legge, si filtra per testo e si scarica. Una sola istantanea per
 * dispositivo: la richiesta seguente la sostituisce.
 */
export function LogDispositivo({ kind, id, nome }: { kind: 'agent' | 'probe'; id: string; nome: string }) {
  const [stato, setStato] = useState<DeviceLog | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [filtro, setFiltro] = useState('')
  const leggi = kind === 'agent' ? api.agentLog : api.probeLog
  const richiedi = kind === 'agent' ? api.agentLogRichiedi : api.probeLogRichiedi
  const percorso = kind === 'agent' ? `/inventory-agents/${id}/logs/download` : `/fleet/probes/${id}/logs/download`

  async function carica() {
    try {
      setStato(await leggi(id, true))
    } catch (e) {
      setErrore(e)
    }
  }
  useEffect(() => { void carica() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // While a request is open, ask again every few seconds: the device answers
  // within a minute, or not at all (off, or a version that does not know it).
  useEffect(() => {
    if (!stato?.pending || stato.expired) return
    const timer = window.setInterval(() => { void carica() }, 5_000)
    return () => window.clearInterval(timer)
  }, [stato?.pending, stato?.expired, stato?.requested_at]) // eslint-disable-line react-hooks/exhaustive-deps

  async function chiedi() {
    setBusy(true)
    setErrore(null)
    try {
      const r = await richiedi(id)
      setStato({ ...(stato ?? r), ...r, content: stato?.content })
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }
  async function scarica() {
    try {
      const blob = await downloadBlob(percorso)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${kind === 'agent' ? 'agente' : 'sonda'}-${nome.replace(/[^\w.-]+/g, '-')}.log`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setErrore(e)
    }
  }

  const righe = useMemo(() => {
    const tutte = (stato?.content ?? '').split('\n')
    const f = filtro.trim().toLowerCase()
    return f ? tutte.filter((r) => r.toLowerCase().includes(f)) : tutte
  }, [stato?.content, filtro])

  return (
    <div className="logdisp">
      <ErrorBanner error={errore} />
      <div className="logdisp__barra">
        <button type="button" onClick={() => void chiedi()} disabled={busy || (stato?.pending && !stato.expired)}>
          {stato?.pending && !stato.expired ? t('In attesa del dispositivo…') : stato?.has_content ? t('Richiedi di nuovo') : t('Richiedi i log')}
        </button>
        {stato?.has_content && (
          <>
            <button type="button" className="secondary" onClick={() => void scarica()}>{t('Scarica')}</button>
            <input type="search" value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder={t('Filtra le righe…')} aria-label={t('Filtra le righe del log')} style={{ minWidth: 220 }} />
          </>
        )}
        <span className="muted eagle-piccolo">
          {stato?.pending && !stato.expired && t('Richiesto {q}: il dispositivo risponde al prossimo contatto.', { q: timeAgo(stato.requested_at ?? '') })}
          {stato?.pending && stato.expired && t('Richiesto {q}, nessuna risposta: il dispositivo è spento, o la sua versione non conosce la richiesta.', { q: timeAgo(stato.requested_at ?? '') })}
          {!stato?.pending && stato?.received_at && t('Ricevuto il {d} · {n} kB{t}{s}', {
            d: formatDateTime(stato.received_at), n: Math.max(1, Math.round(stato.size / 1024)),
            t: stato.truncated ? ` · ${t('solo la coda')}` : '', s: stato.source ? ` · ${stato.source}` : '',
          })}
          {!stato?.pending && !stato?.received_at && stato && t('Nessun log ricevuto finora.')}
        </span>
      </div>
      {stato?.has_content && (
        <pre className="logdisp__testo">{righe.join('\n')}</pre>
      )}
      {stato?.has_content && filtro && (
        <p className="muted eagle-piccolo" style={{ margin: '4px 0 0' }}>{t('{n} righe su {m}', { n: righe.length, m: (stato.content ?? '').split('\n').length })}</p>
      )}
    </div>
  )
}
